'use client'

import React, { useEffect, useRef } from 'react'

// Canvas rather than CSS because the trails need a barely-opaque rectangle painted over the whole
// surface every frame. The three details that make it read: docs/unveiling.md#backgrounds-and-imagery.

/** Printable subset - the source uses charCode(random*128), which yields unrenderable control codes. */
const GLYPHS = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ/\\|:.-+<>[]=*#'
const COL_W = 20
const STEP = 20
const FONT = '15px "DM Mono", ui-monospace, monospace'
// The alpha is trail LENGTH and the one value to tune: remaining opacity after n frames is
// (1 - alpha)^n, so 0.42 leaves a head plus two or three fading glyphs.
const FADE = 'rgba(2, 3, 4, 0.42)'
const INK = 'rgba(0, 255, 111, 0.75)'
const FRAME_MS = 50

export function MatrixRain({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    let stopped = false
    let ypos: number[] = []
    let cssW = 0
    let cssH = 0
    let acc = 0
    let last = 0

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      if (!rect.width || !rect.height) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      cssW = rect.width
      cssH = rect.height
      canvas.width = Math.round(cssW * dpr)
      canvas.height = Math.round(cssH * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.font = FONT
      ctx.textBaseline = 'top'
      const cols = Math.floor(cssW / COL_W) + 1
      // scatter the starting heights so it opens mid-storm rather than as one flat wave
      ypos = Array.from({ length: cols }, () => Math.floor(Math.random() * -cssH))
      ctx.clearRect(0, 0, cssW, cssH)
    }

    const draw = () => {
      ctx.fillStyle = FADE
      ctx.fillRect(0, 0, cssW, cssH)
      ctx.fillStyle = INK
      for (let i = 0; i < ypos.length; i++) {
        const y = ypos[i]
        ctx.fillText(GLYPHS[Math.floor(Math.random() * GLYPHS.length)], i * COL_W, y)
        // randomised reset threshold is the only source of variation between streams
        ypos[i] = y > 120 + Math.random() * 9000 ? 0 : y + STEP
      }
    }

    const tick = (now: number) => {
      if (stopped) return
      acc += last ? Math.min(now - last, 250) : FRAME_MS
      last = now
      if (acc >= FRAME_MS) {
        acc %= FRAME_MS
        draw()
      }
      raf = requestAnimationFrame(tick)
    }

    const play = () => {
      if (stopped || raf) return
      // reset the clock so the first frame back does not replay the whole gap at once
      last = 0
      acc = 0
      raf = requestAnimationFrame(tick)
    }
    const pause = () => {
      cancelAnimationFrame(raf)
      raf = 0
    }

    let ro: ResizeObserver | undefined
    let io: IntersectionObserver | undefined
    const start = () => {
      if (stopped) return
      resize()
      ro = new ResizeObserver(resize)
      ro.observe(canvas)
      // Two instances run per page, and the footer's would otherwise burn frames below the fold.
      io = new IntersectionObserver(
        (entries) => (entries.some((e) => e.isIntersecting) ? play() : pause()),
        { rootMargin: '120px' },
      )
      io.observe(canvas)
    }

    // wait for DM Mono, or the first glyphs paint in a fallback and the slow fade preserves them
    if (document.fonts?.ready) document.fonts.ready.then(start)
    else start()

    return () => {
      stopped = true
      io?.disconnect()
      cancelAnimationFrame(raf)
      ro?.disconnect()
    }
  }, [])

  return <canvas ref={ref} aria-hidden className={className} />
}
